import {
  type DragSourceData,
  type DropTargetData,
  type HorizontalEdge,
  isPanelDragData,
  type PanelDragData
} from './panel-dnd';
import { canDropOnRow, canDropOnRowDropZone, resolveDrop } from './panel-grid';
import { type PageCmsPanel, type PageCmsPanels } from './panel-types';

/** A destination exposed by the non-drag placement workflow. */
export type PanelPlacementPosition =
  | { type: 'row-drop-zone'; rowIndex: number }
  | { type: 'row'; rowIndex: number; panelIndex: number };

export interface PanelPlacementLocation {
  rowIndex: number;
  panelIndex: number;
  rowLength: number;
  isNewRow: boolean;
}

export interface PanelPlacement {
  position: PanelPlacementPosition;
  location: PanelPlacementLocation;
  isCurrent: boolean;
}

const findPanel = (panels: PageCmsPanels, panelId: string) => {
  const rowIndex = panels.findIndex((row) => row.some((panel) => panel.id === panelId));

  return rowIndex === -1
    ? null
    : {
        rowIndex,
        panelIndex: panels[rowIndex].findIndex((panel) => panel.id === panelId),
        rowLength: panels[rowIndex].length
      };
};

const isSourceRow = (source: DragSourceData, rowIndex: number): boolean =>
  isPanelDragData(source) && source.rowIndex === rowIndex;

export const getPanelDragData = (panels: PageCmsPanels, panelId: string): PanelDragData | null => {
  const location = findPanel(panels, panelId);

  return (
    location && {
      type: 'panel',
      panelId,
      rowIndex: location.rowIndex,
      panelIndex: location.panelIndex,
      isOnlyPanelInRow: location.rowLength === 1
    }
  );
};

const getPlacementDropTarget = (
  panels: PageCmsPanels,
  source: DragSourceData,
  position: PanelPlacementPosition
): { target: DropTargetData; edge: HorizontalEdge | null } => {
  if (position.type === 'row-drop-zone') {
    return { target: { type: 'row-drop-zone', rowIndex: position.rowIndex }, edge: null };
  }

  const { rowIndex, panelIndex } = position;

  if (isSourceRow(source, rowIndex)) {
    return { target: { type: 'panel', rowIndex, panelIndex }, edge: null };
  }

  return panelIndex < panels[rowIndex].length
    ? { target: { type: 'panel', rowIndex, panelIndex }, edge: 'left' }
    : { target: { type: 'row', rowIndex }, edge: null };
};

export const resolvePanelPlacement = (
  panels: PageCmsPanels,
  source: DragSourceData,
  position: PanelPlacementPosition,
  incomingPanel: PageCmsPanel
): PageCmsPanels => {
  const { target, edge } = getPlacementDropTarget(panels, source, position);

  return resolveDrop(panels, source, target, edge, () => incomingPanel);
};

const getPlacementPositions = (panels: PageCmsPanels, source: DragSourceData): PanelPlacementPosition[] =>
  Array.from({ length: panels.length + 1 }, (_, rowIndex) => rowIndex).flatMap((rowIndex): PanelPlacementPosition[] => {
    const positions: PanelPlacementPosition[] = canDropOnRowDropZone(source, rowIndex)
      ? [{ type: 'row-drop-zone', rowIndex }]
      : [];
    const row = panels[rowIndex];

    if (!row) {
      return positions;
    }

    const sourceRow = isSourceRow(source, rowIndex);

    if (sourceRow || canDropOnRow(source, rowIndex, row.length)) {
      positions.push(
        ...Array.from({ length: sourceRow ? row.length : row.length + 1 }, (_, panelIndex) => ({
          type: 'row' as const,
          rowIndex,
          panelIndex
        }))
      );
    }

    return positions;
  });

export const getPanelPlacements = (
  panels: PageCmsPanels,
  source: DragSourceData,
  incomingPanel: PageCmsPanel
): PanelPlacement[] =>
  getPlacementPositions(panels, source).flatMap((position) => {
    const next = resolvePanelPlacement(panels, source, position, incomingPanel);
    const location = findPanel(next, incomingPanel.id);

    return location
      ? [
          {
            position,
            location: { ...location, isNewRow: position.type === 'row-drop-zone' },
            isCurrent: next === panels
          }
        ]
      : [];
  });
